<template>
	<div class="status">
		<button
			type="button"
			class="like"
			:class="{ active: currentStatus === 'like' }"
			:aria-pressed="currentStatus === 'like'"
			:aria-label="currentStatus === 'like' ? 'Remove from favorites' : 'Add to favorites'"
			:disabled="disabled"
			@click="handleLikeToggle"
		>
			<AppIcon :name="IconName.Heart" :stroke-width="2" />
			<span class="sr-only">{{ currentStatus === "like" ? "Favorited" : "Favorite" }}</span>
		</button>

		<button
			type="button"
			class="dislike"
			:class="{ active: currentStatus === 'dislike' }"
			:aria-pressed="currentStatus === 'dislike'"
			:aria-label="currentStatus === 'dislike' ? 'Remove from discarded' : 'Discard'"
			:disabled="disabled"
			@click="handleDislikeToggle"
		>
			<AppIcon :name="IconName.EyeOff" :stroke-width="2" />
			<span class="sr-only">{{ currentStatus === "dislike" ? "Discarded" : "Discard" }}</span>
		</button>

		<Teleport to="body">
			<div v-if="showLoginPrompt" class="login-prompt-overlay" @click.self="showLoginPrompt = false">
				<div class="login-prompt">
					<p>{{ t("opportunities.loginPrompt.message") }}</p>
					<div class="actions">
						<Button variant="primary" size="m" @click="confirmLogin">{{ t("opportunities.loginPrompt.login") }}</Button>
						<Button variant="tertiary" size="m" @click="showLoginPrompt = false">{{
							t("opportunities.loginPrompt.later")
						}}</Button>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"
import { EvaluationStatus } from "@/model/DataModel"
import Button from "@/components/ui/Button.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { resolveOpportunityEvaluationStatus } from "@/utils/opportunityEvaluationStatus"

const props = defineProps<{
	opportunityId: number
	initialStatus?: string | null
	projectId?: string
	disabled?: boolean
}>()

const emit = defineEmits<{
	interact: [action: "like" | "dislike"]
}>()

const store = UseRootStore()
const router = useRouter()
const { t } = useI18n()

const currentStatus = computed(() =>
	resolveOpportunityEvaluationStatus(props.opportunityId, props.initialStatus, store.opportunityEvaluations)
)
const showLoginPrompt = ref(false)
const pendingStatus = ref<string | null>(null)

function confirmLogin(): void {
	router.push(`/login?redirect=/?pendingEvaluation=${pendingStatus.value}:${props.opportunityId}`)
}

async function handleLikeToggle(): Promise<void> {
	emit("interact", "like")

	if (!Number.isFinite(props.opportunityId)) return

	if (!store.isUserLoggedIn) {
		pendingStatus.value = EvaluationStatus.Like
		showLoginPrompt.value = true
		return
	}
	const newStatus = currentStatus.value === EvaluationStatus.Like ? null : EvaluationStatus.Like
	await store.updateOpportunityEvaluation(props.opportunityId, { status: newStatus }, props.projectId)
}

async function handleDislikeToggle(): Promise<void> {
	emit("interact", "dislike")

	if (!Number.isFinite(props.opportunityId)) return

	if (!store.isUserLoggedIn) {
		pendingStatus.value = EvaluationStatus.Dislike
		showLoginPrompt.value = true
		return
	}
	const newStatus = currentStatus.value === EvaluationStatus.Dislike ? null : EvaluationStatus.Dislike
	await store.updateOpportunityEvaluation(props.opportunityId, { status: newStatus }, props.projectId)
}
</script>

<style scoped>
.status {
	display: flex;
	align-items: center;
	gap: var(--spacing-xs);
	user-select: none;

	.sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border-width: 0;
	}

	.like,
	.dislike {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-button-s);
		height: var(--size-button-s);
		border: 1px solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		background-color: var(--neutral-0);
		color: var(--neutral-600);
		cursor: pointer;
		padding: 0;
		transition:
			color 0.1s ease,
			background-color 0.1s ease,
			box-shadow 0.1s ease;

		.app-icon {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
		}

		:deep(.ico-heart-fill) {
			display: none;
		}

		&:hover:not(:disabled) {
			box-shadow: var(--shadow-base);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}
	}

	.like.active {
		border-color: var(--accent-favorite);
		background-color: var(--neutral-0);
		color: var(--accent-favorite);

		:deep(.ico-heart-outline) {
			display: none;
		}

		:deep(.ico-heart-fill) {
			display: block;
			fill: currentColor;
		}
	}

	.dislike.active {
		border-color: var(--accent-discarded);
		background-color: var(--neutral-0);
		color: var(--accent-discarded);
	}
}

.login-prompt-overlay {
	position: fixed;
	inset: 0;
	background-color: var(--overlay-backdrop);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;

	.login-prompt {
		background-color: var(--neutral-0);
		border-radius: var(--border-radius-md);
		padding: var(--spacing-lg) var(--spacing-sm) var(--spacing-md);
		max-width: 400px;
		width: 90%;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		box-shadow: var(--shadow-xl);

		p {
			font-size: var(--font-size-sm);
			color: var(--neutral-900);
			text-align: center;
			margin: 0;
		}

		.actions {
			display: flex;
			gap: var(--spacing-sm);
			justify-content: center;

			:deep(.btn) {
				min-width: auto;
			}
		}
	}
}
</style>
